import { BaseApiService } from '@/shared/api/BaseApiService';

export interface UserDTO {
  id: string;
  email: string;
  phone: string | null;
  displayName: string;
  role: string;
  status: string;
  organizationId: string;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  registeredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedUsersResult {
  items: UserDTO[];
  nextCursor?: string;
}

export interface CreateUserInput {
  email: string;
  password: string;
  role: string;
  displayName?: string;
  phone?: string;
  clinicId?: string;
}

export interface SendInvitationInput {
  email: string;
  role: string;
  clinicId: string;
}

export interface CurrentUserProfile {
  id: string;
  email: string;
  displayName: string | null;
  phone: string | null;
  role: string;
  organizationId: string;
  clinicIds: string[];
  activeClinicId: string | null;
  clinics: Array<{ id: string; displayName: string; status: string }>;
}

export interface UpdateMyProfileInput {
  displayName: string;
  phone?: string | null;
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export class UsersService extends BaseApiService {
  protected readonly resourcePath = 'users';

  async listOrgUsers(cursor?: string, limit?: number): Promise<PaginatedUsersResult> {
    return this.get('', { params: { cursor, limit } });
  }

  async listClinicUsers(clinicId: string, cursor?: string, limit?: number): Promise<PaginatedUsersResult> {
    return this.get(`/clinic/${clinicId}`, { params: { cursor, limit } });
  }

  async create(input: CreateUserInput): Promise<UserDTO> {
    return this.post('', input);
  }

  async invite(input: SendInvitationInput): Promise<{ invitationId: string }> {
    return this.post('/invite', input);
  }

  async deactivateUser(userId: string, reason?: string): Promise<{ success: boolean }> {
    return this.post(`/${userId}/deactivate`, { reason });
  }

  async reactivateUser(userId: string): Promise<{ success: boolean }> {
    return this.post(`/${userId}/reactivate`);
  }
}

export class UserProfileService extends BaseApiService {
  protected readonly resourcePath = 'public/auth';

  async getMyProfile(): Promise<CurrentUserProfile> {
    return this.get('/me');
  }

  async updateMyProfile(input: UpdateMyProfileInput): Promise<CurrentUserProfile> {
    return this.patch('/me', input);
  }

  async changePassword(input: ChangePasswordInput): Promise<{ success: boolean }> {
    return this.post('/password/change', input);
  }
}
